import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { ClipboardList, Plus, Loader2, X, Users, Search, KeyRound, MessageCircle, Copy, Trash2 } from "lucide-react";

const eur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const STATO_ORD = {
  programmata: { label: "Programmata", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  aperta: { label: "Aperta", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
  chiusa: { label: "Chiusa", cls: "bg-red-400/15 text-red-300 border-red-400/30" },
};
const STATO_PROD = {
  raccolta_ordini: "Raccolta ordini", ordini_chiusi: "Ordini chiusi", in_preparazione: "In preparazione",
  in_produzione: "In produzione", pronto: "Pronto", consegnato: "Consegnato",
};

export default function RaccolteOrdini() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ feste: [], tipologie: [] });
  const [responsabili, setResponsabili] = useState([]);
  const [filters, setFilters] = useState({ festa: "", tipologia: "", stato_produzione: "", search: "" });
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [respModal, setRespModal] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    params.append("page", page);
    api.get(`/raccolta/raccolte?${params.toString()}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, page]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get("/raccolta/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/raccolta/responsabili").then(({ data }) => setResponsabili(data)).catch(() => {});
  }, []);

  const reloadResp = () => api.get("/raccolta/responsabili").then(({ data }) => setResponsabili(data));

  const delRaccolta = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare definitivamente la raccolta "${r.nome_gruppo}"? Verranno rimossi anche ordini, pagamenti e versamenti collegati.`)) return;
    try { await api.delete(`/raccolta/raccolte/${r.id}`); toast.success("Raccolta eliminata"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight">Raccolta Ordini</h1>
            <p className="text-slate-500 text-sm">Gestione raccolte di gruppo (Fans, Comitati, Band)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="responsabili-btn" onClick={() => setRespModal(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700/40 border border-slate-700 text-slate-200 font-medium text-sm hover:bg-slate-700"><Users className="w-4 h-4" /> Responsabili</button>
          <button data-testid="raccolta-new-btn" onClick={() => setModal(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm"><Plus className="w-4 h-4" /> Nuova Raccolta</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="raccolta-search" value={filters.search} onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, search: e.target.value })); }} placeholder="Cerca gruppo..." className="pl-9 pr-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-cyan-400/60" />
        </div>
        <select data-testid="filter-festa" value={filters.festa} onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, festa: e.target.value })); }} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
          <option value="">Tutte le feste</option>
          {meta.feste.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select data-testid="filter-tipologia" value={filters.tipologia} onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, tipologia: e.target.value })); }} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
          <option value="">Tutte le tipologie</option>
          {meta.tipologie.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="filter-stato-prod" value={filters.stato_produzione} onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, stato_produzione: e.target.value })); }} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
          <option value="">Tutti gli stati</option>
          {Object.entries(STATO_PROD).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento...</div>
      ) : data.items.length === 0 ? (
        <p data-testid="raccolte-empty" className="text-slate-500 text-sm">Nessuna raccolta trovata.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="raccolte-list">
          {data.items.map((r) => {
            const so = STATO_ORD[r.stato_ordini] || STATO_ORD.programmata;
            return (
              <div key={r.id} data-testid={`raccolta-card-${r.id}`} onClick={() => navigate(`/raccolta-ordini/${r.id}`)} className="glass rounded-xl p-4 cursor-pointer hover:border-cyan-400/40 border border-transparent transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-lg font-bold text-slate-100">{r.nome_gruppo}</div>
                    <div className="text-xs text-slate-500">{r.tipologia_gruppo} · {r.festa} · {r.edizione} · resp. {r.responsabile_nome}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${so.cls}`}>{so.label}</span>
                    <button data-testid={`raccolta-del-${r.id}`} onClick={(e) => delRaccolta(e, r)} title="Elimina raccolta" className="p-1.5 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                  <div><div className="text-lg font-bold text-slate-200">{r.num_ordini}</div><div className="text-[10px] uppercase text-slate-500">Ordini</div></div>
                  <div><div className="text-lg font-bold text-emerald-300">{eur(r.valore_ordini)}</div><div className="text-[10px] uppercase text-slate-500">Valore</div></div>
                  <div><div className="text-lg font-bold text-cyan-300">{eur(r.incassato)}</div><div className="text-[10px] uppercase text-slate-500">Incassato</div></div>
                  <div><div className="text-lg font-bold text-amber-300">{eur(r.saldo_da_consegnare)}</div><div className="text-[10px] uppercase text-slate-500">Saldo</div></div>
                </div>
                <div className="text-xs text-slate-500 mt-2">Produzione: {STATO_PROD[r.stato_produzione]}</div>
              </div>
            );
          })}
        </div>
      )}

      {data.total > data.page_size && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 disabled:opacity-40">Precedente</button>
          <span className="text-sm text-slate-400">Pagina {page} / {Math.ceil(data.total / data.page_size)}</span>
          <button disabled={page >= Math.ceil(data.total / data.page_size)} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 disabled:opacity-40">Successiva</button>
        </div>
      )}

      {modal && <NuovaRaccoltaModal meta={meta} responsabili={responsabili} onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
      {respModal && <ResponsabiliModal responsabili={responsabili} onClose={() => setRespModal(false)} reload={reloadResp} />}
    </Layout>
  );
}

function NuovaRaccoltaModal({ meta, responsabili, onClose, onSaved }) {
  const [form, setForm] = useState({ responsabile_id: "", tipologia_gruppo: "Fans", nome_gruppo: "", festa: "", edizione: new Date().getFullYear(), data_ora_apertura: "", data_ora_chiusura: "", note_interne: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.responsabile_id) return toast.error("Seleziona un responsabile");
    if (!form.nome_gruppo.trim()) return toast.error("Nome gruppo obbligatorio");
    if (!form.festa) return toast.error("Seleziona una festa");
    setSaving(true);
    try {
      const payload = { ...form, edizione: parseInt(form.edizione, 10) };
      ["data_ora_apertura", "data_ora_chiusura"].forEach((k) => { payload[k] = form[k] ? new Date(form[k]).toISOString() : null; });
      await api.post("/raccolta/raccolte", payload);
      toast.success("Raccolta creata");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div className="glass rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="raccolta-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-100">Nuova raccolta</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Responsabile</label>
            <select data-testid="raccolta-responsabile" value={form.responsabile_id} onChange={(e) => set("responsabile_id", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100">
              <option value="">Seleziona...</option>
              {responsabili.map((r) => <option key={r.id} value={r.id}>{r.username}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Tipologia</label>
              <select data-testid="raccolta-tipologia" value={form.tipologia_gruppo} onChange={(e) => set("tipologia_gruppo", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100">
                {meta.tipologie.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Festa</label>
              <select data-testid="raccolta-festa" value={form.festa} onChange={(e) => set("festa", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100">
                <option value="">Seleziona...</option>
                {meta.feste.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Nome gruppo</label>
              <input data-testid="raccolta-nome" value={form.nome_gruppo} onChange={(e) => set("nome_gruppo", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Edizione (anno)</label>
              <input data-testid="raccolta-edizione" type="number" value={form.edizione} onChange={(e) => set("edizione", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Apertura</label>
              <input data-testid="raccolta-apertura" type="datetime-local" value={form.data_ora_apertura} onChange={(e) => set("data_ora_apertura", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Chiusura</label>
              <input data-testid="raccolta-chiusura" type="datetime-local" value={form.data_ora_chiusura} onChange={(e) => set("data_ora_chiusura", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Note interne</label>
            <input value={form.note_interne} onChange={(e) => set("note_interne", e.target.value)} className="mt-1.5 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800">Annulla</button>
          <button data-testid="raccolta-save-btn" onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Crea
          </button>
        </div>
      </div>
    </div>
  );
}

function ResponsabiliModal({ responsabili, onClose, reload }) {
  const [form, setForm] = useState({ username: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({}); // { [id]: password } mostrata dopo creazione/reset

  const loginUrl = `${window.location.origin}/accesso-responsabile`;
  const buildMsg = (username) =>
    `Ciao! Ecco l'accesso al portale Gigli Edition per gestire gli ordini del tuo gruppo.\n\n` +
    `🔗 Link: ${loginUrl}\n` +
    `👤 Accedi dalla scheda "Responsabile" e inserisci il nome gruppo: ${username}\n\n` +
    `La password ti viene comunicata a parte. Dal telefono potrai inserire ordini, pagamenti e versamenti.`;
  const waShare = (username) => window.open(`https://wa.me/?text=${encodeURIComponent(buildMsg(username))}`, "_blank");
  const copyText = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); }
    catch { toast.error("Copia non riuscita, seleziona il testo manualmente"); }
  };

  const create = async () => {
    if (!form.username.trim() || form.password.length < 6) return toast.error("Nome gruppo e password (min 6) obbligatori");
    setSaving(true);
    try {
      const { data } = await api.post("/raccolta/responsabili", form);
      toast.success("Responsabile creato");
      setRevealed((s) => ({ ...s, [data.id]: form.password }));
      setForm({ username: "", password: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetPwd = async (r) => {
    const pwd = window.prompt(`Nuova password per ${r.username} (min 6 caratteri):`);
    if (!pwd) return;
    try {
      await api.patch(`/raccolta/responsabili/${r.id}/password`, { password: pwd });
      toast.success("Password aggiornata");
      setRevealed((s) => ({ ...s, [r.id]: pwd }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delResp = async (r) => {
    if (!window.confirm(`Eliminare il responsabile "${r.username}"?`)) return;
    try { await api.delete(`/raccolta/responsabili/${r.id}`); toast.success("Responsabile eliminato"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div className="glass rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="responsabili-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-100">Responsabili</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-2 mb-2">
          <input data-testid="resp-username" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="Nome gruppo" className="flex-1 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100" />
          <input data-testid="resp-password" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" className="w-32 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100" />
          <button data-testid="resp-create-btn" onClick={create} disabled={saving} className="px-3 py-2 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}</button>
        </div>
        <p className="text-[11px] text-slate-500 mb-4">La password non è recuperabile in seguito: annotala o inviala su un canale sicuro. Non viene mai inclusa nel messaggio WhatsApp.</p>
        <div className="space-y-2" data-testid="responsabili-list">
          {responsabili.length === 0 && <p className="text-slate-500 text-sm">Nessun responsabile.</p>}
          {responsabili.map((r) => (
            <div key={r.id} className="bg-slate-900/40 rounded-lg px-3 py-2 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-200 text-sm font-medium">{r.username}</span>
                <div className="flex items-center gap-1.5">
                  <button data-testid={`resp-reset-${r.id}`} onClick={() => resetPwd(r)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-slate-700/40 text-slate-300 border border-slate-700 hover:bg-slate-700"><KeyRound className="w-3.5 h-3.5" /> Reset password</button>
                  <button data-testid={`resp-del-${r.id}`} onClick={() => delResp(r)} title="Elimina responsabile" className="p-1.5 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {revealed[r.id] && (
                <div className="flex items-center justify-between gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-2.5 py-1.5" data-testid={`resp-pwd-${r.id}`}>
                  <span className="text-xs text-emerald-200">Password: <span className="font-mono-tech">{revealed[r.id]}</span></span>
                  <button onClick={() => copyText(revealed[r.id], "Password copiata")} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-500/20 text-emerald-100 border border-emerald-500/40"><Copy className="w-3 h-3" /> Copia</button>
                </div>
              )}
              <div className="flex gap-2">
                <button data-testid={`resp-wa-${r.id}`} onClick={() => waShare(r.username)} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/25"><MessageCircle className="w-3.5 h-3.5" /> Invia accesso WhatsApp</button>
                <button data-testid={`resp-copy-${r.id}`} onClick={() => copyText(buildMsg(r.username), "Istruzioni copiate")} className="inline-flex items-center justify-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-slate-700/40 text-slate-300 border border-slate-700 hover:bg-slate-700"><Copy className="w-3.5 h-3.5" /> Copia istruzioni</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
